import type { ChatInputHandle } from "../../components/ChatInput";
import { PAGES } from "../../context/PageContext";
import { getActionIdentifier } from "../../types/chat";
import { toastError } from "../../utils/toast";
import { type ChatConversation } from "../../utils/web-api";
import { DEFAULT_MODEL } from "@common/components/ModelSelectionDropdown";
import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import type { ChatMessage } from "../../types/chat";
import type { VirtuosoHandle } from "react-virtuoso";
import type { useAnalytics } from "../../context/AnalyticsContext";
import type { ChatScrollTarget } from "src/context/ChatContext";
import type { SendMessageOptions } from "../../context/ChatContext";

const CHAT_INPUT_MOUNT_SETTLE_MS = 100;

export interface UseMessageComposerInputs {
  conversationId: string;
  restoreScrollTargetRef: React.MutableRefObject<{ conversationId: string; actionId: string; }>;
  setPendingScrollTarget: (target: ChatScrollTarget) => void;
  filteredMessagesRef: React.MutableRefObject<ChatMessage[]>;
  virtuosoRef: React.MutableRefObject<VirtuosoHandle>;
  sendError: ReturnType<typeof useAnalytics>["sendError"];
  sendMessage: (message: string, options: SendMessageOptions) => Promise<void>;
  setTodoLists: React.Dispatch<React.SetStateAction<Map<string, any>>>;
  clearStreamingConversation: () => void;
  createAndSelectConversation: (name: string) => Promise<ChatConversation>;
  currentPage: string;
  pageParams: Record<string, any>;
  authHeader: string;
  copiedPromptIdRef: React.MutableRefObject<string>;
  attachPromptFilesToConversation: (promptId: string) => Promise<void>;
}

export function useMessageComposer({ conversationId, restoreScrollTargetRef, setPendingScrollTarget, filteredMessagesRef, virtuosoRef, sendError, sendMessage, setTodoLists, clearStreamingConversation, createAndSelectConversation, currentPage, pageParams, authHeader, copiedPromptIdRef, attachPromptFilesToConversation }: UseMessageComposerInputs) {
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const pendingPromptFilesAttachRef = useRef<Promise<void>>(Promise.resolve());
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  useEffect(() => {
    if (!conversationId) return;

    try {
      const target = restoreScrollTargetRef.current;
      if (target && target.conversationId === conversationId) {
        restoreScrollTargetRef.current = null;
        setPendingScrollTarget(null);
        setAutoScrollEnabled(false);
        requestAnimationFrame(() => {
          const index = filteredMessagesRef.current.findIndex((message, i) =>
            getActionIdentifier(message, `msg-${i}`) === target.actionId
          );
          virtuosoRef.current?.scrollToIndex(
            index === -1 ? { index: "LAST", behavior: "auto" } : { index, align: "start", behavior: "auto" }
          );
        });
        return;
      }

      setAutoScrollEnabled(true);

      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({ index: "LAST", behavior: "auto" });
      });
    } catch (err) {
      sendError(err instanceof Error ? err : new Error(String(err)), {
        component: 'ChatPage',
        action: 'load_conversation_history',
        conversationId
      });
    }
  }, [conversationId, sendError, setPendingScrollTarget, filteredMessagesRef, restoreScrollTargetRef, virtuosoRef]);
  const handleSend = useCallback(async (message: string, selectedSkills?: string[], options?: { agentOnlyContext?: string }): Promise<boolean> => {
    if (!message.trim()) return false
    await pendingPromptFilesAttachRef.current
    await sendMessage(message, {
      selectedSkills,
      selectedModel,
      agentOnlyContext: options?.agentOnlyContext,
      onError: (err) => {
        toastError(err.message)
      },
      onTodoListUpdate: (todoData: any, toolId: string) => {
        setTodoLists(prev => {
          const newMap = new Map(prev)
          const key = todoData.file_path || toolId
          if (todoData.action === "create" || todoData.action === "merge") {
            newMap.set(key, todoData)
          }
          return newMap
        })
      }
    })
    return true
  }, [sendMessage, selectedModel, setTodoLists]);
  const handleInterrupt = useCallback(() => {
    clearStreamingConversation();
  }, [clearStreamingConversation]);
  const clearInputsAndCreateNewConversation = async () => {
    try {
      setAutoScrollEnabled(true);

      chatInputRef.current?.focus();

      const conversation = await createAndSelectConversation("New Conversation");

      return conversation;
    } catch (error) {
      toastError(error);
      sendError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ChatPage',
        action: 'clear_inputs_and_create_new_conversation'
      });
      return null;
    }
  };
  useEffect(() => {
    if (currentPage === PAGES.chatPage && chatInputRef.current) {
      setTimeout(() => {
        try {
          chatInputRef.current?.focus();
        } catch (error) {
          console.warn("Could not focus chat input:", error);
        }
      }, CHAT_INPUT_MOUNT_SETTLE_MS);
    }
  }, [currentPage]);
  useEffect(() => {
    const promptId = pageParams?.promptId as string | undefined;
    if (!promptId || !authHeader || copiedPromptIdRef.current === promptId) return;
    copiedPromptIdRef.current = promptId;
    pendingPromptFilesAttachRef.current = attachPromptFilesToConversation(promptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParams?.promptId, attachPromptFilesToConversation, sendError]);
  return { selectedModel, setSelectedModel, autoScrollEnabled, setAutoScrollEnabled, chatInputRef, handleSend, handleInterrupt, clearInputsAndCreateNewConversation };
}
